import { CheckCircle2, TrendingUp, TrendingDown, Info, Pencil } from 'lucide-react';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { billReport } from '../../lib/billVerdict';
import { useChargeMapContext } from '../../hooks/useChargeMapContext';
import type {
  BvBill, ActualBillResult, ShadowBillResult, ShadowBillSection, CalcExclusion,
} from '../../types/billTypes';
// Every figure and every sentence in this report also has to come out of the
// emailed PDF identically — so both read them from the same module rather than
// each keeping its own copy. See features/billValidation/lib/reportFormulas.ts.
import {
  fmtCurrency as fmt,
  fmtDate,
  fmtVariancePercent as pct,
  classifyVariance as classify,
  accuracyPercent,
  formatAccuracy,
  accuracyCaption,
  autoCustomerMessage,
  verdictDescription,
  buildSiteAddress,
  resolveRetailerName,
  ROW_STATUS_LABEL,
  VERDICT_LABEL,
  MANUAL_NOTES,
  VALIDATION_STATUS_COPY,
  DISCLAIMER_PARAGRAPHS,
  COMPANY_FOOTER,
  type VarianceKind,
} from '../../lib/reportFormulas';

const PRIMARY = '#3790C5';

// Same-origin proxy route — served by our own Express server so html2canvas
// can load it in PDF exports with no CORS restrictions.
const LOGO_SRC = `${import.meta.env.VITE_API_URL ?? 'http://localhost:3001'}/api/logo/choice-energy?v=2`;
const FOOTER_LOGO_SRC = 'https://wqntwtlwqxtovegfmjxe.supabase.co/storage/v1/object/public/templates/logo/choiceEnergyloho1.png';

// Row-level styling for the Charge Section Comparison table — perfect/green both
// read as "good" at a glance; the 100% vs 98-99% distinction only matters for the
// overall bill's customer-facing message, not per line item.
// Colours only; the label text comes from the shared module.
const ROW_STYLE: Record<VarianceKind, { text: string; badge: string }> = {
  perfect: { text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
  green:   { text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
  yellow:  { text: 'text-yellow-600',  badge: 'bg-yellow-100 text-yellow-700'   },
  over:    { text: 'text-red-600',     badge: 'bg-red-100 text-red-700'         },
  under:   { text: 'text-orange-600',  badge: 'bg-orange-100 text-orange-700'   },
};

const SECTION_PILL: Record<ShadowBillSection, { bg: string; label: string }> = {
  retail:        { bg: '#3790C5', label: 'Retail' },
  distribution:  { bg: '#8E44AD', label: 'Distribution' },
  metering:      { bg: '#E67E22', label: 'Metering' },
  environmental: { bg: '#27AE60', label: 'Environmental' },
  other:         { bg: '#95A5A6', label: 'Other' },
  gst:           { bg: '#7F8C8D', label: 'GST' },
};

type Props = {
  bill: BvBill;
  shadowResult: ShadowBillResult;
  actualResult: ActualBillResult;
  retailer?: string;
  /** GST switched out of the comparison. The GST and Subtotal rows are then
   *  dropped rather than printed as $0.00 — a row of zeroes reads as "the
   *  retailer charged no GST", which is a different claim entirely. */
  gstExcluded?: boolean;
  /** Jumps the parent to the Notes tab so the customer-facing note can be edited. */
  onEditNote?: () => void;
  /** The charges switched out, when the host holds them ahead of the bill row
   *  (Bill Run Detail). Defaults to what is stored on the bill. */
  exclusions?: CalcExclusion[];
};

export function BillReportScreen({ bill, shadowResult: sr, actualResult: ar, retailer, gstExcluded = false, onEditNote, exclusions }: Props) {
  const nmi      = bill.site?.nmi ?? '—';
  const client   = bill.site?.client?.name ?? '—';
  const period   = `${fmtDate(bill.bill_from)} – ${fmtDate(bill.bill_to)}`;
  const days     = bill.billing_days ?? '—';
  const address  = buildSiteAddress(bill.site);
  // Was `retailer ?? '—'`, and no caller ever passes the prop — so the report
  // printed "—" even when the invoice parser had read a retailer off the invoice
  // and the site had one recorded. Both fallbacks now apply, here and in the PDF.
  const retailerName = resolveRetailerName(retailer, ar.extracted_retailer, bill.site?.current_retailer);

  // The section rows are the Comparison tab's sections, never the invoice's own
  // headings: the charges are paired through the retailer's charge map exactly
  // as the Comparison table pairs them (comparisonSections.ts). Until the map
  // has loaded the rows are not drawn, and `data-report-ready` stays false so a
  // PDF capture waits rather than sending figures paired without the map.
  //
  // The figures are the compared charges only (billReport, decided 2026-10-07):
  // pass-through charges and add-backs are internal, so the client sees what
  // we validated, its GST and a total that add up. This is also what the PDF
  // and the emailed report carry.
  const { mapContext, ready } = useChargeMapContext(bill, ar);
  const report = ready
    ? billReport({ ...bill, calc_exclusions: exclusions ?? bill.calc_exclusions }, mapContext)
    : null;
  const sectionRows = report?.sections ?? [];
  const expTotal   = report?.total.expected ?? sr.total;
  const invTotal   = report?.total.invoice ?? ar.total;
  const subtotal   = report?.subtotal ?? { expected: sr.subtotal, invoice: ar.subtotal };
  const gst        = report ? report.gst : { expected: sr.gst, invoice: ar.gst };
  const totalDiff  = report?.verdict.variance ?? Math.round((ar.total - sr.total) * 100) / 100;
  const totalKind  = report?.verdict.kind ?? classify(sr.total, ar.total);
  const accuracyPct = accuracyPercent(expTotal, invTotal);
  const accuracyDisplay = formatAccuracy(accuracyPct);
  // GST switched off: the report runs ex-GST, and its Subtotal and GST rows go.
  const showGstRows = report ? report.gst !== null : !gstExcluded;

  const reportDate = bill.report_data?.generated_at
    ? new Date(bill.report_data.generated_at).toLocaleDateString('en-AU', {
        day: '2-digit', month: 'short', year: 'numeric',
      })
    : new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });

  // Palette + icon only — the label and blurb come from the shared module so the
  // emailed PDF says exactly the same thing.
  const VERDICT_STYLE: Record<VarianceKind, { bar: string; bg: string; border: string; text: string; badge: string; Icon: typeof CheckCircle2 }> = {
    perfect: { bar: 'bg-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', badge: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
    green:   { bar: 'bg-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', badge: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
    yellow:  { bar: 'bg-yellow-400',  bg: 'bg-yellow-50',  border: 'border-yellow-200',  text: 'text-yellow-800',  badge: 'bg-yellow-100 text-yellow-800',   Icon: Info },
    over:    { bar: 'bg-red-500',     bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-800',     badge: 'bg-red-100 text-red-800',         Icon: TrendingUp },
    under:   { bar: 'bg-orange-500',  bg: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-800',  badge: 'bg-orange-100 text-orange-800',   Icon: TrendingDown },
  };

  const verdict = {
    ...VERDICT_STYLE[totalKind],
    label: VERDICT_LABEL[totalKind],
    desc:  verdictDescription(totalKind, expTotal, invTotal),
  };

  const { Icon: VerdictIcon } = verdict;
  const isGood = totalKind === 'perfect' || totalKind === 'green';

  const validationStatus = bill.report_data?.validation_status;
  const noteType         = bill.report_data?.customer_note_type;
  const noteManual       = bill.report_data?.customer_note_manual;

  const autoMsg = autoCustomerMessage(totalKind, accuracyDisplay);
  const NOTES = MANUAL_NOTES;

  return (
    <div className="bg-gray-300 py-10 px-4 flex justify-center" style={{ minHeight: '100vh' }}>
    <div data-pdf-target data-report-ready={ready ? 'true' : 'false'} className="bg-white text-gray-900 w-full shadow-2xl" style={{ fontFamily: "'Segoe UI', 'Inter', Arial, sans-serif", fontSize: '18px', lineHeight: '1.7', maxWidth: '794px' }}>

      {/* ── Header bar ────────────────────────────────────────────────── */}
      <div style={{ backgroundColor: PRIMARY }}>
        <div className="px-8 py-7 flex items-center justify-center">
          <img src={LOGO_SRC} alt="Choice Energy" className="w-3/5 h-auto object-contain" />
        </div>
        <p className="text-lg font-bold text-white text-center pb-4">Hi {client},</p>
      </div>

      {/* ── Info Banner ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-8 py-3" style={{ backgroundColor: '#E8B84B' }}>
        <div className="rounded-full border-2 border-current p-2 shrink-0" style={{ color: '#1e3a5f' }}>
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <div>
          <p className="font-black uppercase tracking-wider text-base" style={{ color: '#1e3a5f' }}>
            Helping provide clarity on your energy bill charges
          </p>
          <p className="text-sm italic mt-0.5" style={{ color: '#1e3a5f', opacity: 0.75 }}>
            A bill validation assessment of your latest energy bill
          </p>
        </div>
      </div>

      {/* ── Bill Details Box ─────────────────────────────────────────── */}
      <div className="px-8 py-4 bg-white border-b border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3">
          {[
            { label: 'Customer',       value: client },
            { label: 'Invoice Range',  value: period },
            { label: 'Invoice Length', value: `${days} days` },
            { label: 'NMI',            value: nmi },
            { label: 'Address',        value: address },
            { label: 'Retailer',       value: retailerName },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-0.5">{label}</p>
              <p className="text-base font-semibold text-gray-800 leading-snug">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Bill Accuracy Banner ─────────────────────────────────────── */}
      <div className={cn(
        'flex items-center gap-3 px-8 py-3.5 border-b',
        isGood            ? 'bg-emerald-50 border-emerald-200'
        : totalKind === 'yellow' ? 'bg-yellow-50 border-yellow-200'
        : totalKind === 'under'  ? 'bg-orange-50 border-orange-200'
        :                          'bg-red-50 border-red-200',
      )}>
        <div className={cn(
          'w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-xl shrink-0',
          isGood            ? 'bg-emerald-500'
          : totalKind === 'yellow' ? 'bg-yellow-500'
          : totalKind === 'under'  ? 'bg-orange-500'
          :                          'bg-red-500',
        )}>
          {isGood ? '✓' : totalKind === 'yellow' ? '~' : '!'}
        </div>
        <div>
          <p className="font-black text-gray-900 text-xl">
            Bill Accuracy: {accuracyDisplay}
          </p>
          <p className={cn(
            'text-base mt-0.5',
            isGood            ? 'text-emerald-700'
            : totalKind === 'yellow' ? 'text-yellow-700'
            : totalKind === 'under'  ? 'text-orange-700'
            :                          'text-red-700',
          )}>
            {accuracyCaption(totalKind)}
          </p>
        </div>
      </div>

      <div className="px-8 py-5 space-y-5">

        {/* ── KPI Cards ───────────────────────────────────────────────── */}
        <div data-pdf-avoid-break="true" className="grid grid-cols-3 gap-4">
          {[
            {
              // Both headline figures are ex-GST when GST is switched out of the
              // comparison. Saying so here is the difference between a total the
              // client can reconcile against their invoice and one they cannot.
              label:  'Expected Invoice Total',
              sub:    !showGstRows ? 'Calculated by system, excl. GST' : 'Calculated by system',
              value:  fmt(expTotal),
              color:  'text-gray-900',
              accent: PRIMARY,
            },
            {
              label:  'Retailer Invoice Total',
              sub:    !showGstRows ? 'Charged by retailer, excl. GST' : 'Charged by retailer',
              value:  fmt(invTotal),
              color:  'text-gray-900',
              accent: PRIMARY,
            },
            {
              label:  'Variance',
              sub:    pct(expTotal, invTotal),
              value:  `${totalDiff >= 0 ? '+' : ''}${fmt(totalDiff)}`,
              color:  isGood ? 'text-emerald-700' : totalKind === 'yellow' ? 'text-yellow-700' : totalKind === 'over' ? 'text-red-700' : 'text-orange-700',
              accent: isGood ? '#10b981' : totalKind === 'yellow' ? '#eab308' : totalKind === 'over' ? '#ef4444' : '#f97316',
            },
          ].map(({ label, sub, value, color, accent }) => (
            <div
              key={label}
              className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-0.5 shadow-sm"
              style={{ borderTopColor: accent, borderTopWidth: '4px' }}
            >
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">{label}</p>
              <p className={cn('text-2xl font-black tabular-nums mt-1 leading-tight', color)}>{value}</p>
              <p className="text-sm text-gray-400">{sub}</p>
            </div>
          ))}
        </div>

        {/* ── Charge Section Comparison ───────────────────────────────── */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="h-5 w-1 rounded-full" style={{ backgroundColor: PRIMARY }} />
            <h2 className="text-sm font-black uppercase tracking-[0.2em] text-gray-500">
              Charge Section Comparison
            </h2>
          </div>

          <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
            <table className="w-full text-base">
              <thead>
                <tr style={{ backgroundColor: PRIMARY }}>
                  {['Section', 'Expected Invoice', 'Retailer Invoice', 'Difference', 'Variance %', 'Status'].map((h, i) => (
                    <th
                      key={h}
                      className={cn(
                        'px-4 py-3 text-sm font-bold text-white uppercase tracking-wider',
                        i === 0 ? 'text-left' : 'text-right',
                      )}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!ready && (
                  <tr className="border-t border-gray-100 bg-white">
                    <td colSpan={6} className="px-4 py-3 text-center text-sm text-gray-400">Loading charge sections…</td>
                  </tr>
                )}
                {sectionRows.map(({ key, label, shadow: sv, actual: av }) => {
                  const d    = av - sv;
                  const kind = classify(sv, av);
                  const rs   = ROW_STYLE[kind];
                  return (
                    <tr key={key} className="border-t border-gray-100 bg-white">
                      <td className="px-4 py-3 font-semibold text-gray-800">{label}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-600">{fmt(sv)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-600">{fmt(av)}</td>
                      <td className={cn('px-4 py-3 text-right tabular-nums font-semibold', rs.text)}>
                        {d >= 0 ? '+' : ''}{fmt(d)}
                      </td>
                      <td className={cn('px-4 py-3 text-right tabular-nums', rs.text)}>{pct(sv, av)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={cn('inline-flex items-center gap-1 text-sm font-bold px-2.5 py-1 rounded-md', rs.badge)}>
                          {ROW_STATUS_LABEL[kind]}
                        </span>
                      </td>
                    </tr>
                  );
                })}

                {/* Subtotal and GST, only while GST is part of the comparison.
                    With GST switched out the subtotal IS the total. */}
                {showGstRows && (
                <tr className="border-t-2 border-gray-300 bg-gray-100">
                  <td className="px-4 py-3 font-bold text-gray-700">Subtotal (excl. GST)</td>
                  <td className="px-4 py-3 text-right font-bold tabular-nums text-gray-800">{fmt(subtotal.expected)}</td>
                  <td className="px-4 py-3 text-right font-bold tabular-nums text-gray-800">{fmt(subtotal.invoice)}</td>
                  <td className={cn('px-4 py-3 text-right font-bold tabular-nums', ROW_STYLE[classify(subtotal.expected, subtotal.invoice)].text)}>
                    {subtotal.invoice - subtotal.expected >= 0 ? '+' : ''}{fmt(subtotal.invoice - subtotal.expected)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-500">{pct(subtotal.expected, subtotal.invoice)}</td>
                  <td />
                </tr>
                )}

                {showGstRows && gst && (
                <tr className="border-t border-gray-200 bg-gray-50">
                  <td className="px-4 py-2 text-gray-500">GST (10%)</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">{fmt(gst.expected)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">{fmt(gst.invoice)}</td>
                  <td className={cn('px-4 py-2 text-right tabular-nums', ROW_STYLE[classify(gst.expected, gst.invoice)].text)}>
                    {gst.invoice - gst.expected >= 0 ? '+' : ''}{fmt(gst.invoice - gst.expected)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">{pct(gst.expected, gst.invoice)}</td>
                  <td />
                </tr>
                )}

                {/* Total row */}
                <tr className={cn(
                  'border-t-2',
                  totalKind === 'over'    ? 'bg-red-50 border-red-400'
                  : totalKind === 'under' ? 'bg-orange-50 border-orange-400'
                  : totalKind === 'yellow'? 'bg-yellow-50 border-yellow-400'
                  :                         'bg-emerald-50 border-emerald-400',
                )}>
                  <td className="px-4 py-3 font-black text-gray-900 text-lg uppercase tracking-wide">
                    Total{!showGstRows && <span className="font-bold text-xs normal-case tracking-normal text-gray-500"> (excl. GST)</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-black text-gray-900 text-lg tabular-nums">{fmt(expTotal)}</td>
                  <td className="px-4 py-3 text-right font-black text-gray-900 text-lg tabular-nums">{fmt(invTotal)}</td>
                  <td className={cn('px-4 py-3 text-right font-black text-base tabular-nums',
                    isGood ? 'text-emerald-700' : totalKind === 'yellow' ? 'text-yellow-700' : totalKind === 'over' ? 'text-red-700' : 'text-orange-700',
                  )}>
                    {totalDiff >= 0 ? '+' : ''}{fmt(totalDiff)}
                  </td>
                  <td className={cn('px-4 py-3 text-right font-bold tabular-nums',
                    isGood ? 'text-emerald-700' : totalKind === 'yellow' ? 'text-yellow-700' : totalKind === 'over' ? 'text-red-700' : 'text-orange-700',
                  )}>
                    {pct(expTotal, invTotal)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={cn(
                      'inline-flex items-center text-sm font-black px-3 py-1.5 rounded-md uppercase tracking-wider',
                      verdict.badge,
                    )}>
                      {verdict.label}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Verdict / Overcharge Banner (now above Validation Summary) ─── */}
        <div className={cn('flex border rounded-xl overflow-hidden', verdict.bg, verdict.border)}>
          <div className={cn('w-1.5 shrink-0', verdict.bar)} />
          <div className="flex items-center gap-4 px-8 py-5 flex-1">
            <div className={cn('p-2.5 rounded-full shrink-0', verdict.bar)}>
              <VerdictIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className={cn('text-2xl font-black tracking-tight', verdict.text)}>
                  {verdict.label}
                </span>
                {totalKind !== 'perfect' && (
                  <span className={cn('text-lg font-bold tabular-nums', verdict.text)}>
                    {totalDiff > 0 ? '+' : ''}{fmt(totalDiff)}
                    <span className="text-base font-medium ml-1.5 opacity-70">({pct(expTotal, invTotal)})</span>
                  </span>
                )}
              </div>
              <p className={cn('text-base mt-1 max-w-2xl', verdict.text.replace('800', '700'))}>
                {verdict.desc}
              </p>
            </div>
          </div>
        </div>

        {/* ── Validation Summary ───────────────────────────────────────── */}
        <div data-pdf-avoid-break="true">
          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <div className="h-5 w-1 rounded-full" style={{ backgroundColor: PRIMARY }} />
              <h2 className="text-sm font-black uppercase tracking-[0.2em] text-gray-500">
                Validation Summary
              </h2>
            </div>
            {onEditNote && (
              <button
                type="button"
                onClick={onEditNote}
                className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 hover:underline"
              >
                <Pencil className="h-3 w-3" /> Edit customer note
              </button>
            )}
          </div>

          {/* Manual status override */}
          {validationStatus === 'validated_approved' && (
            <div className="flex gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 mb-4">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-emerald-800 text-base">{VALIDATION_STATUS_COPY.validated_approved.title}</p>
                <p className="text-base text-gray-700 mt-1">
                  {VALIDATION_STATUS_COPY.validated_approved.body}
                </p>
              </div>
            </div>
          )}

          {validationStatus === 'discrepancy_detected' && (
            <div className="flex gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4 mb-4">
              <span className="text-red-600 font-black text-lg shrink-0 leading-none mt-0.5">!</span>
              <div>
                <p className="font-bold text-red-800 text-base">{VALIDATION_STATUS_COPY.discrepancy_detected.title}</p>
                <p className="text-base text-gray-700 mt-1 whitespace-pre-line">{autoMsg}</p>
              </div>
            </div>
          )}

          {validationStatus === 'missing_invoice' && (
            <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 mb-4">
              <span className="text-amber-600 font-black text-lg shrink-0 leading-none mt-0.5">⚠</span>
              <div>
                <p className="font-bold text-amber-800 text-base">{VALIDATION_STATUS_COPY.missing_invoice.title}</p>
                <p className="text-base text-gray-700 mt-1">
                  {VALIDATION_STATUS_COPY.missing_invoice.body}
                </p>
              </div>
            </div>
          )}

          {/* Auto message when no manual override */}
          {!validationStatus && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl px-5 py-4">
              <p className="text-base text-gray-700 leading-relaxed whitespace-pre-line">{autoMsg}</p>
            </div>
          )}
        </div>

        {/* Customer-facing notes (call to action) */}
        {(noteType || noteManual) && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-4">
            <p className="text-sm font-bold uppercase tracking-wider text-blue-700 mb-2">Note from Choice Energy</p>
            {noteType && NOTES[noteType] && (
              <p className="text-base text-gray-700 leading-relaxed whitespace-pre-line mb-2">
                {NOTES[noteType]}
              </p>
            )}
            {noteManual && (
              <p className="text-base text-gray-700 leading-relaxed">{noteManual}</p>
            )}
          </div>
        )}

        {/* ── Disclaimer ──────────────────────────────────────────────── */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-5 py-4">
          <p className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Disclaimer</p>
          <div className="space-y-2">
            {DISCLAIMER_PARAGRAPHS.map((p, i) => (
              <p key={i} className="text-sm text-gray-500 leading-relaxed">{p}</p>
            ))}
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────────────── */}
        <div className="border-t-2 border-gray-200 pt-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <img src={FOOTER_LOGO_SRC} alt="Choice Energy" className="h-16 w-auto object-contain" />
              <div>
                <p className="text-sm font-bold text-gray-700">{COMPANY_FOOTER.address}</p>
                <p className="text-xs text-gray-500 mt-0.5">{COMPANY_FOOTER.registration}</p>
                <p className="text-xs mt-0.5" style={{ color: PRIMARY }}>{COMPANY_FOOTER.website}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400 uppercase tracking-wider font-medium">Date report produced</p>
              <p className="text-base font-bold text-gray-700">{reportDate}</p>
            </div>
          </div>
        </div>

      </div>
    </div>
    </div>
  );
}
